import React from "react";
import ReactDOMServer from "react-dom/server";

import {
	faAt,
	faCheckCircle,
	faCheckSquare,
	faClone,
	faH1,
	faH2,
	faHashtag,
	faIcons,
	faLink,
	faListOl,
	faListUl,
	faParagraph,
	faPhotoVideo,
	faVideo,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faBullseyeArrow } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { compact, flatten, memoize } from "lodash";
import { NodeType } from "prosemirror-model";
import { EditorState, TextSelection, Transaction } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { v4 } from "uuid";

import { getSlashCommandEntries } from "~/integrations/actionItemIntegration";
import { jiraSlashCommand } from "~/integrations/jira/slashCommand";
import { type FeatureFlagKeys, fetchFeature, type FlippySubject } from "~/lib/flippy";
import handleKeyResultCommand from "~/lib/prose/plugins/objectives/slashHandler";
import { changeToPromptHeading, promptHeadingsEnabled, trackSectionEdited } from "~/lib/prose/plugins/prompt";
import { BUILT_IN_SNIPPETS, CUSTOM_TEMPLATE } from "~/lib/prose/snippet";
import { closestBlock, nextBlockInsertionPos } from "~/lib/prose/utils";
import { CtrlOrCmd, isNative, isZoom } from "~/lib/utils";
import { launchCreateTagModal } from "~/tags";

import { setBlockType } from "../commands";
import { ImageStrip, insertImageStrip } from "../nodes/image-strip";
import { embedOptions, launchEmbedModal } from "./integrations/helpers";
import openRecordVideoModal from "./recordVideo/RecordVideoModal";
import { TypeAheadProvider, type TypeAheadResult, type TypeAheadState } from "./typeahead";

import slashItemStyles from "../css/slashItem.module.css";

export type SlashCommandChoice = Omit<TypeAheadResult, "id"> & {
	icon: IconDefinition | React.ReactNode;
	shortcut?: string;
	content: string;
	/** undefined means it might be renderer in plus menu, see menu.tsx */
	showInToolbar?: boolean;
	/** default = true */
	showInSlashMenu?: boolean;
	synonyms?: Array<string>;
	/** If set, the command will only be available if the feature is enabled */
	withFeature?: { id: FeatureFlagKeys; subject: FlippySubject };
	/** If set, the command will only be available if the feature is disabled.
	 * Note that currently the withFeature and withoutFeature are mutually exclusive
	 * where withFeature is prioritized if both are used. */
	withoutFeature?: { id: FeatureFlagKeys; subject: FlippySubject };
	/** If set, the command will not be displayed in the plus menu on >= 736px screens because it's already in the toolbar */
	showOnlyOnMediumScreen?: boolean;
	/** If set, the command will not be displayed in the plus menu on > 280px screens because it's already in the toolbar */
	showOnlyOnExtraSmallScreen?: boolean;
};

const isIconDef = (item: SlashCommandChoice["icon"]): item is IconDefinition => {
	return Boolean(item && typeof item == "object" && "icon" in item);
};

const ChoiceRender = ({
	icon,
	title,
	content,
	shortcut,
}: Pick<SlashCommandChoice, "icon" | "title" | "content" | "shortcut">) => {
	return (
		<div className={slashItemStyles.choiceItem}>
			<div className={slashItemStyles.icon}>
				{!isIconDef(icon) ? <span className={slashItemStyles.textIcon}>{icon}</span> : <FontAwesomeIcon icon={icon} />}
			</div>
			<div className={slashItemStyles.text}>
				<div className={slashItemStyles.title}>{title}</div>
				<p>{content}</p>
			</div>
			<div className={slashItemStyles.shortcut}>{shortcut}</div>
		</div>
	);
};

const getChoices: (isExternalUser: boolean) => SlashCommandChoice[] = memoize(isExternalUser => [
	{
		title: "Talking Point",
		icon: faCheckCircle,
		shortcut: "()",
		content: "Add a discussion item.",
		command: (state: EditorState) => {
			return setBlockType(state.schema.nodes["agenda"], {});
		},
	},
	{
		title: "Action Item",
		icon: faCheckSquare,
		shortcut: "[]",
		content: "Add, assign, schedule tasks.",
		command: (state: EditorState) => {
			return setBlockType(state.schema.nodes["todo"], {});
		},
	},
	{
		title: "Heading 1",
		icon: faH1,
		shortcut: "#",
		content: "Large section heading.",
		command: (state: EditorState) => {
			return setBlockType(state.schema.nodes["header-one"], {});
		},
	},
	{
		title: "Heading 2",
		icon: faH2,
		shortcut: "##",
		content: "Medium section heading.",
		command: (state: EditorState) => {
			return setBlockType(state.schema.nodes["header-one"], { level: "2" });
		},
	},
	{
		title: "Paragraph",
		icon: faParagraph,
		content: "Unstyled simple text.",
		command: (state: EditorState) => {
			return setBlockType(state.schema.nodes["unstyled"], {});
		},
	},
	{
		title: "Bullet list",
		icon: faListUl,
		shortcut: "-",
		content: "Add a bulleted list.",
		showInToolbar: true,
		showOnlyOnExtraSmallScreen: true,
		command: (state: EditorState) => {
			if ("bullet-point" in state.schema.nodes) {
				return setBlockType(state.schema.nodes["bullet-point"], { indent: 1 });
			}
			return setBlockType(state.schema.nodes["unstyled"], {
				indent: 1,
			});
		},
	},
	{
		title: "Numbered List",
		icon: faListOl,
		shortcut: "1.",
		content: "Add a numbered list.",
		showInToolbar: true,
		showOnlyOnExtraSmallScreen: true,
		command: (state: EditorState) => {
			if ("numbered-list-item" in state.schema.nodes) {
				return setBlockType(state.schema.nodes["numbered-list-item"], { indent: 1 });
			}
			return setBlockType(state.schema.nodes["unstyled"], {
				indent: 1,
			});
		},
	},
	...(!promptHeadingsEnabled
		? []
		: flatten(
				[BUILT_IN_SNIPPETS[0], BUILT_IN_SNIPPETS[1]].map(snippet => [
					{
						title: `Section (${snippet.defaultSectionName})`,
						icon: faClone,
						content: `Section with ${snippet.name.toLowerCase()} template.`,
						showInToolbar: false,
						command(state: EditorState) {
							trackSectionEdited(
								state,
								closestBlock(state.selection.$from).attrs.blockId,
								"added",
								`slash_command_${snippet.id}`,
							);
							return changeToPromptHeading(false, {
								promptResponseTemplate: snippet,
							});
						},
					},
					{
						title: `Section (${snippet.defaultSectionName})`,
						icon: faClone,
						content: `Section with ${snippet.name.toLowerCase()} template.`,
						showInToolbar: true,
						showInSlashMenu: false,
						command() {
							return (state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView) => {
								if (!view) return false;

								const { tr } = state;
								const $pos = state.selection.$to;

								if (closestBlock($pos).content.size !== 0) {
									const insertPos = nextBlockInsertionPos(state.selection.$to);
									dispatch(
										tr
											.insert(
												insertPos,
												(state.schema.nodes["header-one"] as NodeType).createChecked({
													blockId: v4(),
												}),
											)
											.setSelection(new TextSelection(tr.doc.resolve(insertPos + 1))),
									);
								}

								trackSectionEdited(
									state,
									closestBlock(view.state.selection.$to).attrs.blockId,
									"added",
									`plus_menu_${snippet.id}`,
								);

								return changeToPromptHeading(false, {
									promptResponseTemplate: snippet,
								})(view.state, view.dispatch, view);
							};
						},
					},
				]),
			).concat([
				{
					title: `Section (Custom)`,
					icon: faClone,
					content: `Section with custom template.`,
					showInToolbar: false,
					command(state: EditorState) {
						trackSectionEdited(
							state,
							closestBlock(state.selection.$from).attrs.blockId,
							"added",
							`slash_command_${CUSTOM_TEMPLATE.id}`,
						);
						return changeToPromptHeading(true, {
							promptResponseTemplate: CUSTOM_TEMPLATE,
						});
					},
				},
				{
					title: `Section (Custom)`,
					icon: faClone,
					content: `Section with custom template.`,
					showInToolbar: true,
					showInSlashMenu: false,
					command() {
						return (state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView) => {
							if (!view) return false;

							const { tr } = state;
							const $pos = state.selection.$to;

							if (closestBlock($pos).content.size !== 0) {
								const insertPos = nextBlockInsertionPos(state.selection.$to);
								dispatch(
									tr
										.insert(
											insertPos,
											(state.schema.nodes["header-one"] as NodeType).createChecked({
												blockId: v4(),
											}),
										)
										.setSelection(new TextSelection(tr.doc.resolve(insertPos + 1))),
								);
							}

							trackSectionEdited(
								state,
								closestBlock(view.state.selection.$to).attrs.blockId,
								"added",
								`plus_menu_${CUSTOM_TEMPLATE.id}`,
							);

							return changeToPromptHeading(true, {
								promptResponseTemplate: CUSTOM_TEMPLATE,
							})(view.state, view.dispatch, view);
						};
					},
				},
			])),
	...(!isZoom && !isNative
		? [
				{
					title: "Record a video",
					content: "If a picture is worth 1000 words...",
					synonyms: ["loom"],
					icon: faVideo,
					showInToolbar: true,
					showOnlyOnMediumScreen: true,
					command: () => {
						return (state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView) => {
							dispatch(state.tr);

							if (view) {
								openRecordVideoModal(view);
							}
							return false;
						};
					},
				},
			]
		: []),
	...(!isZoom
		? [
				{
					title: "Embed media",
					synonyms: embedOptions,
					icon: faIcons,
					content: "Insert media from other websites.",
					showInToolbar: true,
					command:
						() =>
						(state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView): boolean => {
							/* Returning false results in the slash menu not being dismissed.
							 * We could return true, and remove the typeahead text manually,
							 *  but triggering an empty transaction does the trick
							 */
							dispatch(state.tr);

							if (view) launchEmbedModal(view);
							return false;
						},
				},
				{
					title: "Images & video strip",
					content: "Add images and videos.",
					icon: faPhotoVideo,
					replaceMatch: false,
					showInToolbar: true,
					showOnlyOnMediumScreen: true,
					command: (_state: EditorState, typeaheadState?: TypeAheadState) => {
						// @ts-ignore
						return (state: EditorState, dispatch: (tr: Transaction) => void, view) => {
							if (!view) return false;
							const imageStripExt = (view.extensions.get("image_strip") as ImageStrip) || undefined;
							if (!imageStripExt) return false;
							const input = imageStripExt.inputElement;
							input.addEventListener(
								"change",
								_e => {
									if (!input.files) return;
									if (!view) return;
									const stripPos = view.state.doc.resolve(view.state.selection.from).end(1) + 1;
									if (!imageStripExt) return;
									if (imageStripExt) {
										insertImageStrip(stripPos)(state, dispatch);
										imageStripExt.uploadFiles(Array.from(input.files), stripPos + 1);
									}
									// clean up slash command text
									if (typeaheadState?.lastPos && typeaheadState.lastPos > 0) {
										const start = view.state.doc.resolve(typeaheadState.lastPos).start(1);
										const end = view.state.doc.resolve(typeaheadState.lastPos).end(1);
										view.dispatch(view.state.tr.delete(start - 1, end + 1));
									}
								},
								{ once: true },
							);
							input.click();
							return true;
						};
					},
				},
			]
		: []),
	...(!isNative && !isExternalUser ? [jiraSlashCommand, ...getSlashCommandEntries()] : []),
	{
		title: "Mention",
		icon: faAt,
		shortcut: "@",
		content: "Mentions send notifications.",
		showInToolbar: true,
		command: () => {
			return (state: EditorState, dispatch: (tr: Transaction) => void) => {
				dispatch(state.tr.insertText("@"));
				return false;
			};
		},
	},
	{
		title: "Tag",
		icon: faHashtag,
		shortcut: "#",
		content: "Easily find this content later.",
		showInToolbar: true,
		command: () => {
			return (state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView) => {
				// Somehow removes slash menu and corresponding slash command text
				// Borrowed from the embedded media command
				dispatch(state.tr);

				launchCreateTagModal({ view });
				return true;
			};
		},
	},
	{
		title: "Key result",
		icon: faBullseyeArrow,
		content: "Insert an objective’s key result",
		showInToolbar: !isExternalUser,
		showInSlashMenu: !isExternalUser,
		synonyms: ["objectives", "okrs"],
		withoutFeature: { id: "okrs-disabled", subject: "account" },
		command: (_state: EditorState) => {
			return (state: EditorState, dispatch: (tr: Transaction) => void, view?: EditorView): boolean => {
				// TODO: Got this from the embedded media but I don't know what it does, I need to take a look
				// Maybe replaceMatch solves this
				/* Returning false results in the slash menu not being dismissed.
				 * We could return true, and remove the typeahead text manually,
				 *  but triggering an empty transaction does the trick
				 */
				dispatch(state.tr);
				if (view) {
					handleKeyResultCommand(view);
				}
				return false;
			};
		},
	},
	{
		title: "Link",
		content: "Insert a URL link.",
		icon: faLink,
		shortcut: `${CtrlOrCmd}+k`,
		showInToolbar: true,
		command: () => {
			return (state: EditorState, dispatch: (tr: Transaction) => void) => {
				const tr = state.tr;
				const startPos = tr.selection.from;
				const placeholderText = "Link Text";
				tr.addStoredMark(
					state.schema.marks["link"].create({
						entityId: v4(),
						url: "",
					}),
				);
				tr.insertText(placeholderText);
				tr.setSelection(TextSelection.create(tr.doc, startPos, startPos + placeholderText.length));
				dispatch(tr);
				return false;
			};
		},
	},
	{
		title: "Shrug",
		content: "Insert a ¯\\_(ツ)_/¯",
		icon: <span style={{ fontSize: "8px", fontWeight: 900 }}>¯\_(ツ)_/¯</span>,
		showInToolbar: true,
		command: () => {
			return (state, dispatch) => {
				dispatch(state.tr.insertText("¯\\_(ツ)_/¯"));
				return false;
			};
		},
	},
]);

export function choiceToTypeaheadResult(
	choice: SlashCommandChoice,
	id: number | string,
): TypeAheadResult & SlashCommandChoice {
	return {
		...choice,
		id,
		render: (container: HTMLElement) => {
			container.innerHTML = ReactDOMServer.renderToStaticMarkup(<ChoiceRender {...choice} />);
			return true;
		},
	};
}

const getSlashMenuChoices = memoize((isExternalUser: boolean) =>
	compact(getChoices(isExternalUser))
		.filter(choice => choice.showInSlashMenu ?? true)
		.map((choice, index) => choiceToTypeaheadResult(choice, index)),
);

export const getMenuChoices = memoize((isExternalUser: boolean) =>
	compact(getChoices(isExternalUser)).map((choice, index) => {
		return choiceToTypeaheadResult(choice, index);
	}),
);

export class SlashProvider extends TypeAheadProvider {
	constructor(options = {}) {
		super(options);

		getSlashMenuChoices(this.options.isExternalUser ?? false)
			.filter(c => !(this.options.exclude ?? []).includes(c.title))
			.filter(c => {
				if (!c.withFeature && !c.withoutFeature) return true;
				if (c.withFeature) {
					fetchFeature(c.withFeature.id, c.withFeature.subject).then(
						isEnabled => isEnabled && this.options.items.push(c),
					);
				} else if (c.withoutFeature) {
					fetchFeature(c.withoutFeature.id, c.withoutFeature.subject).then(
						isEnabled => !isEnabled && this.options.items.push(c),
					);
				}
				return false;
			})
			.forEach(c => {
				this.options.items.push(c);
			});
	}

	get defaultOptions() {
		return {
			...super.defaultOptions,
			...{
				style: "inline",
				items: [],
			},
		};
	}

	get key() {
		return "/";
	}

	search(_view: EditorView, searchString: string): (SlashCommandChoice & { id: TypeAheadResult["id"] })[] {
		if (searchString === "") {
			return this.options.items;
		}

		const items = [];

		for (const item of this.options.items as (SlashCommandChoice & { id: TypeAheadResult["id"] })[]) {
			const score = this.fuzzy([item.title, ...(item.synonyms || [])], searchString);
			if (score === undefined || score === -Infinity) continue;
			items.push({
				...item,
				order: () => -score,
			});
		}

		return items;
	}
}

export default SlashProvider;
